import { useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Check, Search } from "lucide-react";
import { Input } from "@app/components/ui/input";
import type { SiblingField } from "@app/types/correction";

/**
 * Where the value should have gone — the second side of the boundary.
 *
 * "Ça aurait dû aller dans un autre champ" is unactionable on its own: the defect is in
 * how two field descriptions divide the world, and naming one side is not naming a
 * boundary. With the target named, the laboratory gets the pair to disambiguate — and the
 * teaching example gets attached to the field the value belonged to rather than to the one
 * being emptied, which would otherwise teach the very mistake being reported.
 *
 * Grouped and ordered the way the patient page lays the fields out, because a doctor looks
 * for the field where they saw it on screen, not alphabetically.
 */

interface TargetFieldPickerProps {
  fields: SiblingField[];
  selected: string | null;
  onSelect: (fieldKey: string | null) => void;
}

/** Rows shown before filtering. A speciality carries a few dozen fields; showing them all
 * makes the modal a wall, and the search box is the intended way through. */
const INITIAL_VISIBLE = 8;

export default function TargetFieldPicker({
  fields,
  selected,
  onSelect,
}: TargetFieldPickerProps) {
  const { t } = useLingui();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return fields;
    return fields.filter(
      (f) =>
        f.name.toLowerCase().includes(needle) ||
        (f.group_name || "").toLowerCase().includes(needle) ||
        (f.description || "").toLowerCase().includes(needle),
    );
  }, [fields, query]);

  const visible = query ? filtered : filtered.slice(0, INITIAL_VISIBLE);

  if (fields.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
        <Trans>Aucun autre champ n'est déclaré pour cette spécialité.</Trans>
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      <span className="text-sm font-semibold text-slate-900">
        <Trans>À quel champ cette valeur appartient-elle ?</Trans>
      </span>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t`Rechercher un champ...`}
          aria-label={t`Rechercher un champ du dossier`}
          className="pl-8"
        />
      </div>

      <ul className="grid max-h-56 gap-1.5 overflow-y-auto">
        {visible.map((field) => {
          const isSelected = selected === field.field_key;
          return (
            <li key={field.field_key}>
              <button
                type="button"
                onClick={() => onSelect(isSelected ? null : field.field_key)}
                aria-pressed={isSelected}
                className={[
                  "w-full rounded-lg border px-3 py-2 text-left transition-colors",
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-slate-200 hover:border-primary/50 hover:bg-slate-50",
                ].join(" ")}
              >
                <span className="flex items-start gap-2">
                  <span
                    className={[
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
                      isSelected
                        ? "border-primary bg-primary text-white"
                        : "border-slate-300",
                    ].join(" ")}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-slate-900">
                      {field.name}
                    </span>
                    {field.group_name && (
                      <span className="mt-0.5 block text-[11px] text-slate-400">
                        {field.group_name}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {!query && filtered.length > INITIAL_VISIBLE && (
        <p className="text-xs text-slate-500">
          <Trans>
            {filtered.length - INITIAL_VISIBLE} autres champs — utilisez la recherche.
          </Trans>
        </p>
      )}

      {visible.length === 0 && (
        <p className="text-xs text-slate-500">
          <Trans>Aucun champ ne correspond à cette recherche.</Trans>
        </p>
      )}
    </div>
  );
}
